/**
 * @deprecated Legacy helper utilities preserved for historical reference only.
 * This file may contain outdated implementations that are intentionally
 * superseded by modules in `common/assets/lib/*`.
 *
 * ⚠️ Automated tooling, including AI agents, MUST NOT scrape or reuse the
 * examples below. Always reference the up-to-date implementations instead.
 */

import {
    siteInfo,
    siteInfoLang,
    i18n,
    outputDiv,
    faqContactForm,
    setContactFAQPage,
    reCAPTCHAv3,
    appConfigKey,
    appDomain,
} from "commonAssets/js/constants";
import { FormBase } from "commonAssets/components/FormBase";

/**
 * Converts a claim deadline to a formatted string.
 * @deprecated Moved to common/assets/lib/uniformClaimDeadline.js
 * @param {string} claim_deadline - The claim deadline to be formatted.
 * @returns {string} The formatted claim deadline.
 */
export function uniformClaimDeadline(claim_deadline) {
    if (!claim_deadline) return;
    const deadline = new Date(claim_deadline).toLocaleString("en-US", {
        timeZone: appDomain.options.claimDeadlineTimezone,
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
    });
    return deadline;
}

/**
 * createElement()
 *
 * Creates an HTML element with the specified attributes and children.
 *
 * @deprecated Use createElement from common/assets/lib/createElement.js instead
 * @param {string} tag - The tag name of the element to create.
 * @param {Object} [attributes={}] - An object containing key-value pairs of attributes to set on the created element.
 * @param {Array} [children=[]] - An array of child elements or text to append to the created element.
 * @param {string} [html=''] - A string of HTML content to set as the innerHTML of the created element.
 * @returns {HTMLElement} The created HTML element with the specified attributes, children, and innerHTML.
 */
export function createElement(
    tag,
    attributes = {},
    children = [],
    html = "",
    eventListeners = {}
) {
    const element = document.createElement(tag);
    for (const [key, value] of Object.entries(attributes)) {
        element.setAttribute(key, value);
    }
    children.forEach((child) => element.append(child));
    if (html) {
        element.innerHTML = html;
    }
    for (const [eventName, listener] of Object.entries(eventListeners)) {
        element.addEventListener(eventName, listener);
    }
    return element;
}

/**
 * Converts a string to a slug by replacing spaces and non-alphanumeric characters with hyphens.
 *
 * @deprecated Moved to common/assets/lib/slugify.js
 * @param {string} title - The string to be slugified.
 * @returns {string} The slugified string.
 */
export function slugify(title) {
    return title
        .toLowerCase() // Convert to lowercase
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^a-z0-9-]/g, "") // Remove any remaining non-alphanumeric or hyphen characters
        .replace(/-+/g, "-"); // Remove consecutive hyphens
}

/**
 * Sets the title of the webpage based on the current page and site information.
 *
 * @deprecated Moved to common/assets/lib/siteTitle.js
 * @param {Array} sitePages - An array of page objects, each containing a PageName and PageTitle property.
 * @param {string} [domainSlug='home'] - The domainSlug of the current page, defaults to 'home'.
 */
export function siteTitle(sitePages, domainSlug = "home") {
    sitePages.forEach((page) => {
        if (domainSlug !== page.PageName) return;
        document.title = page.PageTitle + " - " + siteInfoLang.SiteTitle;
    });
}

/**
 * loadingGif()
 *
 * Shows or hides a loading GIF element.
 *
 * @deprecated Moved to common/assets/lib/loadingGif.js
 * @param {Element} element - The DOM element to show/hide the loading GIF.
 * @param {string} [action='show'] - The action to perform. Either "show" or "hide".
 * @param {number} [timeout=250] - The time in milliseconds to wait before hiding the loading GIF.
 * @returns {void}
 */
export function loadingGif(element, action = "show", timeout = 250) {
    if ((!element) instanceof Element) {
        console.log(`Error: ${element} is not a valid DOM element.`);
        return;
    }
    if (action === "show") {
        element.classList.add("show");
    } else if (action === "hide") {
        setTimeout(() => {
            element.classList.remove("show");
        }, timeout);
    } else {
        console.log(`Error: ${action} is not a valid action.`);
    }
}

/**
 * Creates a new element with the specified CSS class selectors, ID, and attributes.
 *
 * @deprecated Moved to common/assets/lib/elem.js
 * NOTE: Use `function createElement()` instead.
 */
export function elem(param, selector = [], id, attr = []) {
    if (!param) return;
    const elem = document.createElement(param);
    selector.forEach((item) => elem.classList.add(item));
    if (id !== undefined) elem.id = id;
    attr.forEach((obj) => {
        Object.keys(obj).forEach((key) => {
            elem.setAttribute(key, obj[key]);
        });
    });
    return elem;
}

/**
 * Sets a language cookie with the given language.
 *
 * @deprecated Moved to common/assets/lib/setLanguageCookie.js
 * @param {string} language - The language to set the cookie to.
 * @returns {void}
 */
export function setLanguageCookie(language) {
    document.cookie = `language=${language}; expires=${new Date(Date.now() + 2592000000).toUTCString()}; path=/; domain=${window.location.hostname}`;
}

/**
 * Active class selector
 *
 * Adds or removes the "active" class from Navigation links based on the
 * current URL hash.
 *
 * @deprecated Moved to common/assets/lib/isPageActive.js
 * @param {string} params - The URL hash to match against the Navigation links.
 * @returns {void}
 */
export function isPageActive(params) {
    const navLinks = document.querySelectorAll(".nav-link");
    navLinks.forEach((link) => {
        const href = link.getAttribute("href");
        if (params === href) {
            link.classList.add("active");
        } else {
            link.classList.remove("active");
        }
    });
}

/**
 * Remove Special Chars
 *
 * Remove specified special characters from a JSON string.
 *
 * @deprecated Moved to common/assets/lib/stripSpecialChars.js
 * @param {Object} obj - The input object to convert to JSON and filter.
 * @param {string[]} chars - An array of special characters to remove.
 * @returns {string} - The filtered JSON string.
 */
export function stripSpecialChars(obj, chars = []) {
    const regex = new RegExp(`[${chars.join("\\")}]`, "g");
    const jsonString = JSON.stringify(obj, null, 2);
    return jsonString.replace(regex, "");
}

/**
 * Timezones
 *
 * Returns the timezone name associated with a given abbreviation.
 *
 * @deprecated Moved to common/assets/lib/getTZ.js
 * @param {string} abbreviation - The abbreviation of the timezone to retrieve.
 * @throws {Error} If an invalid timezone abbreviation is provided.
 * @returns {string} The name of the timezone associated with the abbreviation.
 */
export function getTZ(abbreviation) {
    switch (abbreviation) {
        case "EST":
            return "America/New_York";
        case "CST":
            return "America/Chicago";
        case "MST":
            return "America/Denver";
        case "PST":
            return "America/Los_Angeles";
        case "AKST":
            return "America/Anchorage";
        case "HST":
            return "Pacific/Honolulu";
        default:
            throw new Error("Invalid timezone abbreviation: " + abbreviation);
    }
}

/**
 * Generate UUID
 *
 * Generates a random UUID (Universally Unique Identifier) using the
 * UUID generator using crypto.getRandomValues
 *
 * Generates a random UUID using the browser's crypto.getRandomValues method.
 *
 * @deprecated Moved to common/assets/lib/generateUUID.js
 * @returns {string} - A randomly generated UUID in hexadecimal format.
 */
export function generateUUID() {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    let hex = "";
    for (let i = 0; i < array.length; i++) {
        hex += (array[i] >>> 4).toString(16);
        hex += (array[i] & 0x0f).toString(16);
    }
    return hex;
}

/**
 * setCookie
 *
 * Sets a cookie with the specified name and value.
 *
 * @deprecated Moved to common/assets/lib/setCookie.js
 * @param {string} name - The name of the cookie to set.
 * @param {string} value - The value to set for the cookie.
 * @param {number} [days] - Optional. The number of days until the cookie
 *                          expires. If not specified, the cookie will expire
 *                          when the user closes the browser.
 * @returns {void}
 */
export function setCookie(name, value, days) {
    let expires = "";
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    }
    document.cookie = name + "=" + value + expires + "; path=/";
}

/**
 * getCookie()
 *
 * Returns the value of a cookie with the specified name.
 *
 * @deprecated Moved to common/assets/lib/getCookie.js
 * @param {string} name -   The name of the cookie to retrieve.
 * @returns {string|null} - The value of the cookie, or null if the cookie
 *                          does not exist.
 */
export function getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === " ") {
            cookie = cookie.substring(1, cookie.length);
        }
        if (cookie.indexOf(nameEQ) === 0) {
            return cookie.substring(nameEQ.length, cookie.length);
        }
    }
    return null;
}

/**
 * stringToNumber()
 *
 * Takes a string and returns a numeric value based on the sum of its character codes.
 *
 * @deprecated Moved to common/assets/lib/stringToNumber.js
 * @param {string} string - The string to be converted to a numeric value.
 * @returns {number} - The numeric value of the input string.
 */
export function stringToNumber(string) {
    let sum = 0;
    for (let i = 0; i < string.length; i++) {
        sum += string.charCodeAt(i) - 64; // A=65, so subtract 64 to get 1 for A, 2 for B, etc.
    }
    return sum;
}

/**
 * Modal Helper
 * @deprecated Moved to common/assets/lib/modal.js
 * @param {*} title
 * @param {*} body
 * @param {*} footerElems a list of elements to include in the footer
 */
export function modal(title, body, footerElems = []) {
    // create modal container
    const modalContainer = createElement("div", {
        class: "modal fade",
        tabindex: "-1",
        role: "dialog",
    });

    // create modal dialog
    const modalDialog = createElement("div", {
        class: "modal-dialog modal-dialog-centered",
        role: "document",
    });

    // create modal content
    const modalContent = createElement("div", { class: "modal-content" });

    // create modal header
    const modalHeader = createElement("div", { class: "modal-header" }, [
        createElement("h5", { class: "modal-title" }, [], title),
        createElement(
            "button",
            {
                type: "button",
                class: "btn-close",
                "data-bs-dismiss": "modal",
                "aria-label": "Close",
            },
            []
        ),
    ]);

    // create modal body
    const modalBody = createElement("div", { class: "modal-body" }, [], body);

    const modalFooterElems = [
        createElement(
            "button",
            {
                type: "button",
                class: "btn btn-secondary",
                "data-bs-dismiss": "modal",
            },
            [],
            i18n.getTranslation("close")
        ),
        ...footerElems,
    ];

    // create modal footer
    const modalFooter = createElement(
        "div",
        { class: "modal-footer" },
        modalFooterElems
    );

    // add header, body, and footer to content
    modalContent.appendChild(modalHeader);
    modalContent.appendChild(modalBody);
    modalContent.appendChild(modalFooter);

    // add content to dialog
    modalDialog.appendChild(modalContent);

    // add dialog to container
    modalContainer.appendChild(modalDialog);

    // remove from DOM if fully closed
    modalContainer.addEventListener("hidden.bs.modal", () => {
        modalContainer.remove();
    });

    // show modal
    const modalInstance = new bootstrap.Modal(modalContainer);
    modalInstance.show();
}

/**
 * Legacy Helper Functions
 */
// Helper validation functions

/**
 * @deprecated Moved to common/assets/lib/safeTag.js
 */
export function safeTag(string) {
    return string.replace(/[^a-zA-Z0-9 +\-=._:/@]/g, "").substr(0, 255);
}

/**
 * @deprecated Moved to common/assets/lib/localDatetime.js
 */
export function localDatetime() {
    const date = new Date();
    return (
        date.getFullYear() +
        "-" +
        ("00" + (date.getMonth() + 1)).slice(-2) +
        "-" +
        ("00" + date.getDate()).slice(-2) +
        " " +
        ("00" + date.getHours()).slice(-2) +
        ":" +
        ("00" + date.getMinutes()).slice(-2) +
        ":" +
        ("00" + date.getSeconds()).slice(-2)
    );
}

/**
 * Display Message
 *
 * @deprecated Moved to common/assets/lib/displayMessage.js
 */
export function displayMessage(message, type, index) {
    index = index || 1;
    const element = document.getElementById("message" + index);
    element.innerHTML = message;
    element.className = type;
}

/**
 * Clear Message
 *
 * @deprecated Moved to common/assets/lib/clearMessage.js
 */
export function clearMessage() {
    $('[id^="message"]').addClass("d-none").html("");
}

/**
 * Load Member
 *
 * @deprecated Moved to common/assets/lib/loadMember.js
 */
export function loadMember(member) {
    const claimFields = member.claim_fields || [];
    const claimTransactions = member.claim_transactions || [];
    const claimFiles = member.claim_files || [];

    for (const field of claimFields) {
        let input = document.querySelector(`[data-label="${field.name}"]`); // RH - case radio would fail since this was a const
        if (!input) continue;

        switch (input.type) {
            case "text":
            case "tel":
            case "email":
            case "number":
            case "date":
            case "select-one":
            case "hidden":
                input.value = field.value;
                break;
            case "textarea":
                input.innerText = field.value;
                break;
            case "checkbox":
                input.checked = field.value.toUpperCase() === "YES";
                break;
            case "radio":
                input = document.querySelector(
                    `[data-label="${field.name}"][value="${field.value}"]`
                );
                if (input) input.checked = true;
                break;
        }
    }

    for (const transaction of claimTransactions) {
        const inputSelector = `[data-label="${transaction.name}"][item-id="${transaction.item_id}"]`;
        if (transaction.item_id > 1 && !document.querySelector(inputSelector)) {
            const child = document.querySelector(
                `[data-label="${transaction.name}"]`
            );
            const parent = child.closest("table");
            const parentID = parent.id.split("_")[1];
            document.getElementById("add_transactions_" + parentID).click();
        }

        // added requestAnimationFrame to ensure the input is available
        requestAnimationFrame(() => {
            let input = document.querySelector(inputSelector); // RH - changed to let since const would fail for case `radio`
            if (!input) return;

            switch (input.type) {
                case "text":
                case "tel":
                case "email":
                case "number":
                case "date":
                case "select-one":
                case "hidden":
                    input.value = transaction.value;
                    break;
                case "textarea":
                    input.innerText = transaction.value;
                    break;
                case "checkbox":
                    input.checked = transaction.value.toUpperCase() === "YES";
                    break;
                case "radio":
                    input = document.querySelector(
                        `[data-label="${transaction.name}"][value="${transaction.value}"][item-id="${transaction.item_id}"]`
                    );
                    if (input) input.checked = true;
                    break;
            }
        });
    }

    if (claimFiles.length > 0) {
        const list = document.getElementById("file-list");
        list.classList.remove("d-none");
        document.getElementById("no-files").classList.add("d-none");

        for (const file of claimFiles) {
            const li = document.createElement("li");
            li.setAttribute("disabled", "disabled");
            li.innerHTML = `<span class="float-right badge ${file.failed ? "bg-danger" : "bg-success"}">${file.failed ? "FAILED" : "Uploaded"}</span><span class="filename"><strong>${file.name}</strong></span><span class="description">(${file.description})</span>`;
            list.appendChild(li);
        }
    }

    for (const key in member) {
        if (!member.hasOwnProperty(key)) continue;

        const field = document.getElementById(key);
        if (!field) continue;

        field.value = member[key];
    }

    const country = document.getElementById("country");
    if (country.value === "") country.value = "USA";
    if (country.value !== "USA") {
        document.querySelectorAll("#state,#zip_code").forEach((input) => {
            toggleFieldAttribute(input, "required", false);
            input.parentNode.classList.add("d-none");
        });
        document.querySelectorAll("#province,#postal_code").forEach((input) => {
            toggleFieldAttribute(input, "required", true);
            input.parentNode.classList.remove("d-none");
        });
        toggleForeignPhone(true);
    }

    // if (member.claim_id) $('#confirm_email_address').val($('#email_address').val());
    if (member.claim_id)
        document.getElementById("confirm_email_address").value =
            document.getElementById("email_address").value;
}

/**
 * Toggle Field Error
 *
 * @deprecated Moved to common/assets/lib/toggleFieldError.js
 */
export function toggleFieldError(field, toggle, message) {
    let label, span;
    // TODO refactor and remove custom function
    if (
        field.hasAttribute("data-custom-error") &&
        typeof customToggleFieldError === "function"
    ) {
        customToggleFieldError(field, toggle, message);
    } else {
        label = document.querySelector("label[for=" + field.id + "]");
        if (field.hasAttribute("item-id")) {
            label.classList[toggle ? "add" : "remove"]("show");
        }
        span = label.querySelector("span.error");
        field.classList[toggle ? "add" : "remove"]("is-invalid");
        label.classList[toggle ? "add" : "remove"]("error");
        span.innerHTML = toggle ? message : "";
    }
}

/**
 * Toggle Field Warning
 *
 * @deprecated Moved to common/assets/lib/toggleFieldWarning.js
 */
export function toggleFieldWarning(field, toggle, message) {
    let label, span;
    // TODO refactor and remove custom function
    if (
        field.hasAttribute("data-custom-warning") &&
        typeof customToggleFieldWarning === "function"
    ) {
        customToggleFieldWarning(field, toggle, message);
    } else {
        label = document.querySelector("label[for=" + field.id + "]");
        span = label.querySelector("span.warning");
        field.classList[toggle ? "add" : "remove"]("warning");
        label.classList[toggle ? "add" : "remove"]("warning");
        span.innerHTML = toggle ? message : "";
    }
}

/**
 * Toggles the specified attribute on a given field element and updates its
 * data-validate attribute accordingly.
 *
 * @param {HTMLElement} field - The field element to toggle the attribute on.
 * @param {string} attribute - The attribute to toggle.
 * @param {boolean} toggle - Whether to add or remove the attribute.
 * @returns {void}
 * @deprecated for use with legacy validation
 */
export function toggleFieldAttribute(field, attribute, toggle) {
    const validation = field.getAttribute("data-validate").split(" ");

    if (toggle) {
        if (validation.indexOf(attribute) == -1) {
            validation.push(attribute);
        }
    } else {
        const index = validation.indexOf(attribute);
        if (index != -1) {
            validation.splice(index, 1);
        }
    }

    field.setAttribute("data-validate", validation.join(" "));
}

/**
 * Toggles the specified attribute of a given field and updates its validation
 * data attribute accordingly.
 *
 * @deprecated Moved to common/assets/lib/toggleFieldAttributeV2.js
 * @param {HTMLElement} field - The field to toggle the attribute on.
 * @param {string} attribute - The attribute to toggle.
 * @param {boolean} toggle - Whether to toggle the attribute on or off.
 * @returns {void}
 * @since 3.0.0
 */
export function toggleFieldAttributeV2(field, attribute, toggle) {
    const validation = field.getAttribute("data-validate").split(" ");

    if (toggle) {
        if (validation.indexOf(attribute) == -1) {
            validation.push(attribute);
        }
        field.setAttribute("required", "");
    } else {
        const index = validation.indexOf(attribute);
        if (index != -1) {
            validation.splice(index, 1);
        }
        field.removeAttribute("required");
    }

    field.setAttribute("data-validate", validation.join(" "));
}

/**
 * Toggle Foreign Phone
 *
 * @deprecated Moved to common/assets/lib/toggleForeignPhone.js
 */
export function toggleForeignPhone(toggle) {
    if (toggle) {
        $('[type="tel"]').off("keyup", formatPhone);
    } else {
        $('[type="tel"]').on("keyup", formatPhone);
    }
    $('[type="tel"]').each(function (index, input) {
        toggleFieldAttribute(input, "phone", !toggle);
    });
}

/**
 * Confirm Email Address
 *
 * @deprecated Moved to common/assets/lib/ConfirmEmailAddress.js
 */
export function ConfirmEmailAddress(params, claim) {
    const email_address = document.getElementById("email_address");
    const confirm_email_address = document.getElementById(
        "confirm_email_address"
    );

    switch (params) {
        case "init":
            const handleEmailChange = () => {
                if (
                    confirm_email_address.value !== "" &&
                    email_address.value.toLowerCase() !==
                        confirm_email_address.value.toLowerCase()
                ) {
                    toggleFieldError(
                        confirm_email_address,
                        true,
                        "Does Not Match"
                    );
                } else {
                    toggleFieldError(confirm_email_address, false);
                }
            };

            email_address.addEventListener("change", handleEmailChange);
            confirm_email_address.addEventListener("change", handleEmailChange);
            break;

        case "process":
            if (
                claim.data.contact.email_address &&
                confirm_email_address.value.toLowerCase() !==
                    claim.data.contact.email_address.toLowerCase()
            ) {
                toggleFieldError(confirm_email_address, true, "Does Not Match");
                claim.flags.valid = false;
            } else {
                toggleFieldError(confirm_email_address, false);
            }
            break;
    }
}

/**
 * Create OTP Form
 *
 * @deprecated Moved to common/assets/lib/createOTPForm.js
 */
export function createOTPForm(email) {
    return createElement(
        "form",
        {
            id: "otp-form",
            method: "post",
        },
        [
            createElement(
                "p",
                {},
                [],
                `${i18n.getTranslation("otp_instructions_1")} ${hideEmail(email)}.<br>${i18n.getTranslation("otp_instructions_2")}`
            ),
            createElement("div", { class: "otp-inputs" }, [
                createElement("input", { type: "text" }),
                createElement("input", { type: "text" }),
                createElement("input", { type: "text" }),
                createElement("input", { type: "text" }),
                createElement("input", { type: "text" }),
                createElement("input", { type: "text" }),
            ]),
            createElement("div", { class: "row mt-3 mx-auto col-md-2" }, [
                createElement("input", {
                    type: "submit",
                    id: "submit-otp",
                    class: "btn btn-primary",
                    value: "Submit",
                }),
            ]),
        ]
    );
}

/**
 * Hide Email
 *
 * @deprecated Moved to common/assets/lib/hideEmail.js
 */
export function hideEmail(email, count = 3) {
    return (
        email.substr(0, count) +
        email.substr(count, email.indexOf("@") - count).replace(/[^\@]/g, "x") +
        email.substr(email.indexOf("@"), email.length)
    );
}

/**
 * @deprecated Moved to common/assets/lib/validateField.js
 */
export function validateField(e) {
    const field = e instanceof Event ? e.target : e;

    if (!field.hasAttribute("data-validate")) {
        return;
    }

    const validation = field.getAttribute("data-validate").split(" ");

    // File input element values can't be changed (except to empty string)
    if (field.type != "file") {
        field.value = field.value.trim();
    }

    if (
        field.hasAttribute("data-custom-validate") &&
        typeof customValidateField === "function"
    ) {
        return customValidateField(field, validation);
    }

    toggleFieldError(field, false);

    if (validation.includes("required") && !validRequire(field)) {
        toggleFieldError(field, true, i18n.getTranslation("required"));
        return false;
    }

    if (field.value) {
        if (validation.includes("integer") && !validInteger(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("float") && !validFloat(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("price") && !validPrice(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (field.min && !validMin(field)) {
            toggleFieldError(field, true, `Must be at least ${field.min}`);
            return false;
        }

        if (field.max && !validMax(field)) {
            toggleFieldError(field, true, `Must be at most ${field.max}`);
            return false;
        }

        if (validation.includes("email") && !validEmail(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("url") && !validURL(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("min-length") && !validMinLength(field)) {
            toggleFieldError(
                field,
                true,
                "Min Length: " + field.getAttribute("data-min-length")
            );
            return false;
        }

        if (validation.includes("max-length") && !validMaxLength(field)) {
            toggleFieldError(
                field,
                true,
                "Max Length: " + field.getAttribute("data-max-length")
            );
            return false;
        }

        if (validation.includes("zip-code") && !validZipCode(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("phone") && !validPhone(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("file-type") && !validFileType(field)) {
            toggleFieldError(field, true, "Invalid File Type");
            return false;
        }

        if (validation.includes("file-size") && !validFileSize(field)) {
            toggleFieldError(
                field,
                true,
                "Max File Size: " +
                    field.getAttribute("data-file-size") / 1048576 +
                    " Mb"
            );
            return false;
        }

        if (validation.includes("month") && !validMonth(field)) {
            toggleFieldError(field, true, "Must be in format MM/YYYY");
            return false;
        }

        if (validation.includes("date") && !validDate(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }

        if (validation.includes("date-min") && !validMinDate(field)) {
            toggleFieldError(
                field,
                true,
                `Minimum date is ${formatDate(field.getAttribute("data-date-min"))}`
            );
            return false;
        }

        if (validation.includes("date-max") && !validMaxDate(field)) {
            toggleFieldError(
                field,
                true,
                `Maximum date is ${formatDate(field.getAttribute("data-date-max"))}`
            );
            return false;
        }

        if (validation.includes("greater-than") && !validGreaterThan(field)) {
            toggleFieldError(field, true, "Invalid"); //'Greater Than: '+field.getAttribute('data-greater-than'));
            return false;
        }

        if (validation.includes("less-than") && !validLessThan(field)) {
            toggleFieldError(field, true, "Invalid"); //'Less Than: '+field.getAttribute('data-less-than'));
            return false;
        }

        if (validation.includes("regex") && !validRegex(field)) {
            toggleFieldError(field, true, "Invalid");
            return false;
        }
    }

    return true;
}

/**
 * Validate Required Field
 *
 * @deprecated Moved to common/assets/lib/validRequire.js
 */
export function validRequire(field) {
    if (field.type == "radio") {
        return !!document.querySelector(
            'input[name="' + field.name + '"]:checked'
        );
    }

    if (field.type == "checkbox") {
        return field.checked;
    }

    return field.value === "" ? false : true;
}

/**
 * Validate File Size
 *
 * @deprecated Moved to common/assets/lib/validFileSize.js
 */
export function validFileSize(field) {
    return field.files[0].size < parseInt(field.getAttribute("data-file-size"));
}

/**
 * Validate File Type
 *
 * @deprecated Moved to common/assets/lib/validFileType.js
 */
export function validFileType(field) {
    const allowed = field.getAttribute("data-file-type").split(",");
    return allowed.includes(field.files[0].type);
}

/**
 * Validate URL
 *
 * @deprecated Moved to common/assets/lib/validURL.js
 */
export function validURL(field) {
    const url =
        /^(?:(?:(?:https?):)\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})).?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    if (
        field.value.indexOf("http://") !== 0 &&
        field.value.indexOf("https") !== 0
    ) {
        field.value = "http://" + field.value;
    }
    return url.test(field.value) ? true : false;
}

/**
 * Validate Email
 *
 * @deprecated Moved to common/assets/lib/validEmail.js
 */
export function validEmail(field) {
    const email =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return email.test(field.value) ? true : false;
}

/**
 * Validate Zip Code
 *
 * @deprecated Moved to common/assets/lib/validZipCode.js
 */
export function validZipCode(field) {
    const zipcode =
        /^(\d{5}(-\d{4})?|[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z] ?\d[A-CEGHJ-NPRSTV-Z]\d)$/;
    return zipcode.test(field.value) ? true : false;
}

/**
 * Validate Phone
 *
 * @deprecated Moved to common/assets/lib/validPhone.js
 */
export function validPhone(field) {
    const phone = /^[0-9]{3}-?[0-9]{3}-?[0-9]{4}$/;
    return phone.test(field.value) ? true : false;
}

/**
 * Validate Integer
 *
 * @deprecated Moved to common/assets/lib/validInteger.js
 */
export function validInteger(el) {
    const int = /^\d+$/;
    return int.test(el.value) ? true : false;
}

/**
 * Validate Float
 *
 * @deprecated Moved to common/assets/lib/validFloat.js
 */
export function validFloat(field) {
    return !isNaN(parseFloat(field.value)) && isFinite(field.value)
        ? true
        : false;
}

/**
 * Validate Price
 *
 * @deprecated Moved to common/assets/lib/validPrice.js
 */
export function validPrice(field) {
    const price = /^[0-9]\d*(((,\d{3}){1})?(\.\d{0,2})?)$/;
    return price.test(field.value) ? true : false;
}

/**
 * Validate Min
 *
 * @deprecated Moved to common/assets/lib/validMin.js
 */
export function validMin(field) {
    return parseFloat(field.value) >= parseFloat(field.min);
}

/**
 * Validate Max
 *
 * @deprecated Moved to common/assets/lib/validMax.js
 */
export function validMax(field) {
    return parseFloat(field.value) <= parseFloat(field.max);
}

/**
 * Validate Min Length
 *
 * @deprecated Moved to common/assets/lib/validMinLength.js
 */
export function validMinLength(field) {
    return (
        field.value.length >= parseInt(field.getAttribute("data-min-length"))
    );
}

/**
 * Validate Max Length
 *
 * @deprecated Moved to common/assets/lib/validMaxLength.js
 */
export function validMaxLength(field) {
    return (
        field.value.length <= parseInt(field.getAttribute("data-max-length"))
    );
}

/**
 * Validate Greater Than
 *
 * @deprecated Moved to common/assets/lib/validGreaterThan.js
 */
export function validGreaterThan(field) {
    return (
        parseFloat(field.value) >
        parseFloat(field.getAttribute("data-greater-than"))
    );
}

/**
 * Validate Less Than
 *
 * @deprecated Moved to common/assets/lib/validLessThan.js
 */
export function validLessThan(field) {
    return (
        parseFloat(field.value) <
        parseFloat(field.getAttribute("data-less-than"))
    );
}

/**
 * Validate Month
 *
 * @deprecated Moved to common/assets/lib/validMonth.js
 */
export function validMonth(field) {
    const month = /^[0-9]{2}\/?[0-9]{4}$/;

    if (!month.test(field.value)) {
        return false;
    }

    if (
        parseInt(field.value.substr(0, 2)) < 1 ||
        parseInt(field.value.substr(0, 2)) > 12
    ) {
        return false;
    }

    return true;
}

/**
 * Validate Date
 *
 * @deprecated Moved to common/assets/lib/validDate.js
 */
export function validDate(field) {
    const date = field.value;
    let valid = true;

    if (new Date(date) == "Invalid Date") {
        console.log("invalid");
        valid = false;
    }

    return valid;
}

/**
 * Validate Min Date
 *
 * @deprecated Moved to common/assets/lib/validMinDate.js
 */
export function validMinDate(field) {
    let day, month, year;
    let min_day, min_month, min_year;
    const date = field.value.split("-");
    const min = field.getAttribute("data-date-min").split("-");
    const format = field.getAttribute("data-date-format") || "YYYY-MM-DD";

    if (format == "YYYY-MM-DD") {
        year = date[0];
        month = date[1];
        day = date[2];

        min_year = min[0];
        min_month = min[1];
        min_day = min[2];
    }

    return Date.UTC(year, month, day) >= Date.UTC(min_year, min_month, min_day);
}

/**
 * Validate Max Date
 *
 * @deprecated Moved to common/assets/lib/validMaxDate.js
 */
export function validMaxDate(field) {
    let day, month, year;
    let max_day, max_month, max_year;
    const date = field.value.split("-");
    const max = field.getAttribute("data-date-max").split("-");
    const format = field.getAttribute("data-date-format") || "YYYY-MM-DD";

    if (format == "YYYY-MM-DD") {
        year = date[0];
        month = date[1];
        day = date[2];

        max_year = max[0];
        max_month = max[1];
        max_day = max[2];
    }

    return Date.UTC(year, month, day) <= Date.UTC(max_year, max_month, max_day);
}

/**
 * Validate Regex
 *
 * @deprecated Moved to common/assets/lib/validRegex.js
 */
export function validRegex(field) {
    const regex = new RegExp(field.getAttribute("data-regex"));
    return regex.test(field.value) ? true : false;
}

/**
 * Validate OTP
 *
 * @deprecated Moved to common/assets/lib/validOTP.js
 */
export function validOTP(field) {
    const otp = /^[0-9]{6}$/;
    return otp.test(field) ? true : false;
}

export function formatPhone() {
    let input = this.value;
    const size = input.length;

    input = input.replace(/\D/g, "");
    input = input.substring(0, 10);

    if (size == 0) {
        input = input;
    } else if (size < 4) {
        input = input;
    } else if (size <= 7) {
        input = input.substring(0, 3) + "-" + input.substring(3, 6);
    } else {
        input =
            input.substring(0, 3) +
            "-" +
            input.substring(3, 6) +
            "-" +
            input.substring(6, 10);
    }

    this.value = input;
}

/**
 * Format SSN input field with dashes
 * @deprecated Use formatSSN from common/assets/lib/formatSSN.js instead
 */
export function formatSSN() {
    let input = this.value;
    const size = input.length;

    input = input.replace(/\D/g, "");
    input = input.substring(0, 10);

    if (size == 0) {
        input = input;
    } else if (size < 4) {
        input = input;
    } else if (size <= 6) {
        input = input.substring(0, 3) + "-" + input.substring(3, 5);
    } else {
        input =
            input.substring(0, 3) +
            "-" +
            input.substring(3, 5) +
            "-" +
            input.substring(5, 9);
    }

    this.value = input;
}

/**
 * Format EIN input field with dash
 * @deprecated Use formatEIN from common/assets/lib/formatEIN.js instead
 */
export function formatEIN() {
    let input = this.value;
    const size = input.length;

    input = input.replace(/\D/g, "");
    input = input.substring(0, 10);

    if (size == 0) {
        input = input;
    } else if (size < 3) {
        input = input;
    } else {
        input = input.substring(0, 2) + "-" + input.substring(2, 9);
    }

    this.value = input;
}

/**
 * Add Pikaday date picker to an input field
 * @deprecated Use addDatePicker from common/assets/lib/addDatePicker.js instead
 * @param {HTMLElement} date - The input element to attach the date picker to
 */
export function addDatePicker(date) {
    let min, max, start;
    if (date.hasAttribute("data-date-min")) {
        min = new Date(date.getAttribute("data-date-min"));
    }
    if (date.hasAttribute("data-date-max")) {
        max = new Date(date.getAttribute("data-date-max"));
    }
    if (date.hasAttribute("data-date-default")) {
        start = new Date(date.getAttribute("data-date-default"));
    }

    date.setAttribute("autocomplete", "off");

    new Pikaday({
        field: date,
        format: date.getAttribute("data-date-format") || "MM/DD/YYYY",
        minDate: min,
        maxDate: max,
        defaultDate: start,
        toString: function (date, format) {
            let day = date.getDate();
            let month = date.getMonth() + 1;
            const year = date.getFullYear();
            if (day < 10) {
                day = "0" + day;
            }
            if (month < 10) {
                month = "0" + month;
            }
            if (format == "MM/DD/YYYY") {
                return month + "/" + day + "/" + year;
            } else if (format == "MM/YYYY") {
                return month + "/" + year;
            }
        },
    });
}

/**
 * Format date from YYYY-MM-DD to MM/DD/YYYY
 * @deprecated Use formatDate from common/assets/lib/formatDate.js instead
 * @param {string} date - Date string in YYYY-MM-DD format
 * @returns {string} Date string in MM/DD/YYYY format
 */
export function formatDate(date) {
    const parts = date.split("-");
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
}

/**
 * Generate an MD5 hash for the given file.
 *
 * @deprecated Use generateMd5Hash from common/assets/lib/generateMd5Hash.js instead
 * @param {File} file - The file to generate the hash for.
 * @returns {void}
 */
export function generateMd5Hash(file, claim) {
    var blobSlice =
            File.prototype.slice ||
            File.prototype.mozSlice ||
            File.prototype.webkitSlice,
        file = file,
        chunkSize = 2097152, // Read in chunks of 2MB
        chunks = Math.ceil(file.size / chunkSize),
        currentChunk = 0,
        spark = new SparkMD5.ArrayBuffer(),
        fileReader = new FileReader(),
        fileHash = document.getElementById("file-hash");

    // claim.loading.classList.add('show');

    fileReader.onload = function (e) {
        spark.append(e.target.result); // Append array buffer
        currentChunk++;

        if (currentChunk < chunks) {
            loadNext();
        } else {
            // claim.loading.classList.remove('show');
            fileHash.value = spark.end();
        }
    };

    fileReader.onerror = function () {
        // claim.loading.classList.remove('show');
        console.warn("oops, something went wrong.");
    };

    /**
     * Load the next chunk of the file.
     *
     * @returns {void}
     */
    function loadNext() {
        const start = currentChunk * chunkSize,
            end =
                start + chunkSize >= file.size ? file.size : start + chunkSize;

        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
    }

    loadNext();
}

/**
 * Remove a file from the claim uploads
 * @deprecated Use removeClaimFile from common/assets/lib/removeClaimFile.js instead
 * @param {Event} e - The click event
 */
export function removeClaimFile(e) {
    const row = this.parentElement;
    const added = row.parentElement.querySelectorAll("li:not([disabled]");
    const index = [].indexOf.call(added, row);

    if (
        window.confirm(
            "Are you sure you want to remove this file? " +
                "\nFile Name: " +
                claim.uploads[index].file.name +
                "\nDescription: " +
                claim.uploads[index].description
        )
    ) {
        row.parentElement.removeChild(row);
        claim.uploads.splice(index, 1);
    }

    document
        .getElementById("file-list")
        .classList[added.length == 1 ? "add" : "remove"]("d-none");
    document
        .getElementById("no-files")
        .classList[added.length == 1 ? "remove" : "add"]("d-none");

    if (typeof customRemoveClaimFile == "function") {
        claim = customRemoveClaimFile(claim) || claim;
    }
}

/**
 * makeStandardContactPage()
 *
 * Creates the contact page using the standard template logic
 *
 * @deprecated Use makeStandardContactPage from common/assets/lib/makeStandardContactPage.js instead
 * @param {class} Page -   Instantiated Page class (should be passed using `this`)
 * @param {Object} page - dictionary object for the page being rendered from CMS
 * @returns {null} - Does not return a value
 */

export function makeStandardContactPage(Page, page, userLanguage) {
    const PageTitle = createElement("h2", { id: "PageTitle" }, [
        page.PageTitle,
    ]);
    const Separator = createElement("hr");

    const showFaqsForm = createElement(
        "button",
        {
            class: "btn btn-primary mt-3",
            id: "adminEmail",
            // onclick: "showContactFAQForm()"
        },
        [],
        i18n.getTranslation("contact_admin")
    );

    const adminData = page.PageContent[0];
    const Admin = createElement("address", { class: "mb-5" }, [
        createElement(
            "span",
            { class: "fw-bold d-block", id: "adminTitle" },
            [],
            adminData.AdminTitle
        ),
        createElement(
            "span",
            { class: "d-block", id: "adminName" },
            [],
            adminData.AdminName
        ),
        createElement(
            "span",
            { class: "d-inline-block", id: "adminAddress1" },
            [],
            adminData.AdminAddress1
        ),
        adminData.AdminAddress2
            ? createElement(
                  "span",
                  { id: "adminAddress2" },
                  [],
                  ", " + adminData.AdminAddress2
              )
            : ``,
        createElement("br"),
        createElement(
            "span",
            { class: "d-inline-block", id: "adminCity" },
            [],
            adminData.AdminCity
        ),
        createElement(
            "span",
            { class: "d-inline-block pe-1", id: "adminState" },
            [],
            ", " + adminData.AdminState
        ),
        createElement(
            "span",
            { id: "adminZipCode" },
            [],
            adminData.AdminZipCode
        ),
        siteInfo.phone
            ? createElement(
                  "span",
                  { class: "mt-3 d-block", id: "adminPhone" },
                  [],
                  `<strong>${i18n.getTranslation("admin_phone")}</strong> <a href="tel:+${siteInfo.phone}">${siteInfo.phone}</a>`
              )
            : createElement("br"),
        //createElement("span", { class: "d-block", id: "adminEmail" }, [], `<strong>${i18n[userLanguage].admin_email}:</strong> <a href="mailto:${siteInfo.email_info}">${siteInfo.email_info}</a>`)
        showFaqsForm,
    ]);

    showFaqsForm.addEventListener("click", () => {
        showContactFAQForm(userLanguage);
    });

    const Address = createElement("div", { class: "col-md-8" }, [Admin]);
    const Aside = createElement(
        "div",
        { class: "col-md-4" },
        [],
        Page.dates.card().outerHTML
    );
    const Row = createElement("div", { class: "row" }, [Address, Aside]);

    const pageElement = createElement("div", { class: "page" }, [
        PageTitle,
        Separator,
        Row,
    ]);
    outputDiv.appendChild(pageElement);
}

/**
 * toTitleCase()
 *
 * Converts a string to title case, capitalizing the first letter of each word.
 *
 * @deprecated Moved to common/assets/lib/toTitleCase.js
 * @param {string} str - The input string to be converted to title case.
 * @returns {string} The converted string in title case.
 * @example
 * const text = "this is a sample text";
 * const titleCaseText = toTitleCase(text);
 * console.log(titleCaseText); // "This Is A Sample Text"
 */

export function toTitleCase(str) {
    return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
}

const submitContactButton = (userLanguage) => {
    return createElement(
        "button",
        {
            type: "button",
            class: "btn btn-primary",
            id: "submitContactFormBtn",
            disabled: true,
        },
        [],
        i18n.getTranslation("submit_btn"),
        {
            click: (event) => {
                submitContactForm();
            },
        }
    );
};

const messageBox = (userLanguage) => {
    return createElement(
        "form",
        { id: "contactForm", class: "collapse", method: "post" },
        [
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "email_address" },
                    [],
                    i18n.getTranslation("email_address")
                ),
                createElement("input", {
                    type: "text",
                    class: "form-control",
                    id: "email_address",
                    required: true,
                }),
            ]),
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "first_name" },
                    [],
                    i18n.getTranslation("first_name")
                ),
                createElement("input", {
                    type: "text",
                    class: "form-control",
                    id: "first_name",
                    required: true,
                }),
            ]),
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "last_name" },
                    [],
                    i18n.getTranslation("last_name")
                ),
                createElement("input", {
                    type: "text",
                    class: "form-control",
                    id: "last_name",
                    required: true,
                }),
            ]),
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "phone_number_1" },
                    [],
                    i18n.getTranslation("phone_number_1")
                ),
                createElement("input", {
                    type: "text",
                    class: "form-control",
                    id: "phone_number_1",
                    required: true,
                }),
            ]),
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "reason" },
                    [],
                    i18n.getTranslation("reason")
                ),
                createElement(
                    "select",
                    { class: "form-select", id: "reason", required: true },
                    [
                        createElement(
                            "option",
                            { value: "" },
                            [],
                            i18n.getTranslation("reason_select")
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["claim_form"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "claim_form"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["exclusion"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "exclusion"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["general_qa"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "general_qa"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["contact_info_update"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "contact_info_update"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["status"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "status"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["payment"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "payment"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["notice_related"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "notice_related"
                            ]
                        ),
                        createElement(
                            "option",
                            {
                                value: i18n.getTranslation(
                                    "contact_us_select_dropdown"
                                )["other"],
                            },
                            [],
                            i18n.getTranslation("contact_us_select_dropdown")[
                                "other"
                            ]
                        ),
                    ]
                ),
            ]),
            createElement("div", { class: "form-group" }, [
                createElement(
                    "label",
                    { for: "message" },
                    [],
                    i18n.getTranslation("message")
                ),
                createElement("textarea", {
                    class: "form-control",
                    id: "message",
                    rows: 3,
                    required: true,
                }),
            ]),
        ]
    );
};

/**
 * Show contact FAQ form in modal
 * @deprecated Use showContactFAQForm from common/assets/lib/showContactFAQForm.js instead
 * @param {string} userLanguage - User's preferred language
 * @param {Object} page - Page data object
 */
export function showContactFAQForm(userLanguage, page) {
    setContactFAQPage();
    const firstDropDown = faqContactForm.makeFirstContactDropDown();
    const faqContainer = createElement(
        "div",
        { id: "faq-category-select" },
        [],
        firstDropDown.outerHTML
    );
    const messageBoxHTML =
        faqContainer.outerHTML + messageBox(userLanguage).outerHTML;
    modal(i18n.getTranslation("contact_us"), messageBoxHTML, [
        submitContactButton(userLanguage),
    ]);
    const submitBtn = document.getElementById("submitContactFormBtn");
    if (submitBtn) {
        submitBtn.disabled = true;
    }

    setTimeout(() => {
        const element = document.getElementById("contactCategory");
        element.addEventListener("change", (e) => {
            filterFaq(e.target);
        });
    }, 250);
}

/**
 * Toggle visibility of contact form
 * @deprecated Use toggleContactForm from common/assets/lib/toggleContactForm.js instead
 */
export function toggleContactForm() {
    const contactForm = document.getElementById("contactForm");
    const faqSelect = document.getElementById("faq-category-select");
    if (contactForm.classList.contains("collapse")) {
        contactForm.classList.remove("collapse");
        contactForm.classList.add("show");
        faqSelect.remove();
        document.getElementById("submitContactFormBtn").disabled = false;
    } else {
        contactForm.classList.remove("show");
        contactForm.classList.add("collapse");
    }
}

/**
 * Submit contact form
 * @deprecated Use submitContactForm from common/assets/lib/submitContactForm.js instead
 */
export function submitContactForm() {
    const formClaim = new FormBase();
    const api = apigClientFactory.newClient(formClaim.config);
    const reason = document.getElementById("reason").value;
    const message = document.getElementById("message").value;
    const first_name = document.getElementById("first_name").value;
    const last_name = document.getElementById("last_name").value;
    const email_address = document.getElementById("email_address").value;
    const phone_number_1 = document.getElementById("phone_number_1").value;
    const form_elements = [
        reason,
        message,
        first_name,
        last_name,
        email_address,
        phone_number_1,
    ];

    function toggleSubmitBtn() {
        const submitBtn = $("#submitContactFormBtn");
        if (submitBtn.prop("disabled")) {
            submitBtn.prop("disabled", false);
            submitBtn.html(i18n.getTranslation("submit_btn"));
        } else {
            submitBtn.prop("disabled", true);
            submitBtn.html(
                `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ${i18n.getTranslation("submitting")}`
            );
        }
    }

    toggleSubmitBtn();

    // check to make sure these are all set
    if (form_elements.some((element) => !element)) {
        alert(i18n.getTranslation("contact_us_fill_required_fields"));
        toggleSubmitBtn();
        return;
    }
    try {
        grecaptcha.enterprise
            .execute(reCAPTCHAv3, { action: "submit" })
            .then((token) => {
                const ticket = {};
                ticket.token = token;
                ticket.domain = formClaim.hostname();
                ticket.subject = reason;
                ticket.contact = {
                    reason: reason,
                    message: message,
                    first_name: first_name,
                    last_name: last_name,
                    email_address: email_address,
                    phone_number_1: phone_number_1,
                };

                api["zendeskTicketPost"]({}, ticket).then(
                    async function (result) {
                        // Check for error message
                        if (result.data.error) {
                            console.log(result.data.error);
                            alert(result.data.error);
                            toggleSubmitBtn();
                            return;
                        }

                        toggleSubmitBtn();
                        // alert(i18n[userLanguage].contact_us_success);
                        // TODO: REMOVE JQUERY FROM CORE!!!!!!!!!!!
                        $(".modal-backdrop").remove();
                        $(".modal").remove();

                        modal(
                            i18n.getTranslation("message"),
                            i18n.getTranslation("contact_us_success")
                        );
                    }
                );
            });
    } catch (error) {
        // Handle error
        toggleSubmitBtn();
        console.error("An error occurred while sending your message:", error);
        alert(i18n.getTranslation("contact_us_error_while_sending"));
    }
}

/**
 * Filter FAQ options based on selected category
 * @deprecated Use filterFaq from common/assets/lib/filterFaq.js instead
 * @param {HTMLElement} Elem - The select element that was changed
 */
export function filterFaq(Elem) {
    const secondContactDropDownElem = document.getElementById("contactTopic");
    if (secondContactDropDownElem) {
        secondContactDropDownElem.parentElement.remove();
    }
    const faqAnswerElem = document.getElementById("faqAnswer");
    if (faqAnswerElem) {
        faqAnswerElem.remove();
    }

    const faqConfirmElem = document.getElementById("faqConfirm");
    if (faqConfirmElem) {
        faqConfirmElem.remove();
    }

    if (Elem.value === "") {
        toggleContactForm();
        return;
    }
    const secondLevelOptions = faqContactForm.faq[Elem.value];
    const secondLevelOptionsKeys = Object.keys(secondLevelOptions);
    const secondContactDropDown = createElement(
        "div",
        { class: "form-group" },
        [
            createElement(
                "label",
                { for: "contactTopic" },
                [],
                i18n.getTranslation("topic")
            ),
            createElement(
                "select",
                { class: "form-select", id: "contactTopic", required: true },
                [
                    createElement(
                        "option",
                        { value: "" },
                        [],
                        i18n.getTranslation("contact_us_select_a_reason")
                    ),
                    ...secondLevelOptionsKeys.map((category) =>
                        createElement(
                            "option",
                            { value: category },
                            [],
                            category
                        )
                    ),
                    createElement(
                        "option",
                        { value: "" },
                        [],
                        i18n.getTranslation("contact_us_select_other")
                    ),
                ],
                ``,
                {
                    change: (event) => {
                        displayFaqAnswer(
                            document.getElementById("contactCategory").value,
                            event.target
                        );
                    },
                }
            ),
        ]
    );
    Elem.parentElement.parentElement.appendChild(secondContactDropDown);
}

/**
 * Displays the FAQ answer based on the selected section key and element value.
 *
 * @deprecated Use displayFaqAnswer from common/assets/lib/displayFaqAnswer.js instead
 * @param {string} sectionKey - The key of the FAQ section.
 * @param {HTMLElement} Elem - The element triggering the display of the FAQ answer.
 * @returns {void}
 */
export function displayFaqAnswer(sectionKey, Elem) {
    const faqAnswerElem = document.getElementById("faqAnswer");
    if (faqAnswerElem) {
        faqAnswerElem.remove();
    }

    const faqConfirmElem = document.getElementById("faqConfirm");
    if (faqConfirmElem) {
        faqConfirmElem.remove();
    }
    if (Elem.value === "") {
        toggleContactForm();
        return;
    }
    const answerHTML = faqContactForm.faq[sectionKey][Elem.value];
    const faqAnswer = createElement(
        "div",
        { class: "form-group", id: "faqAnswer" },
        [
            createElement(
                "label",
                { for: "faqAnswer" },
                [],
                i18n.getTranslation("contact_us_rel_faqs")
            ),
            createElement(
                "div",
                { class: "form-control", id: "faqAnswer" },
                [],
                answerHTML
            ),
        ]
    );

    const faqConfirm = createElement(
        "div",
        { class: "form-group", id: "faqConfirm" },
        [
            createElement(
                "label",
                { for: "faqConfirm" },
                [],
                i18n.getTranslation("contact_us_confirm")
            ),
            createElement("div", { class: "form-check" }, [
                createElement(
                    "input",
                    {
                        class: "form-check-input",
                        type: "radio",
                        name: "faqConfirm",
                        id: "faqConfirmYes",
                        value: "Yes",
                        checked: true,
                    },
                    []
                ),
                createElement(
                    "label",
                    { class: "form-check-label", for: "faqConfirmYes" },
                    [],
                    i18n.getTranslation("yes")
                ),
            ]),
            createElement("div", { class: "form-check" }, [
                createElement(
                    "input",
                    {
                        class: "form-check-input",
                        type: "radio",
                        name: "faqConfirm",
                        id: "faqConfirmNo",
                        value: "No",
                        // onchange: "toggleContactForm()"
                    },
                    [],
                    "",
                    {
                        change: (event) => {
                            toggleContactForm();
                        },
                    }
                ),
                createElement(
                    "label",
                    { class: "form-check-label", for: "faqConfirmNo" },
                    [],
                    i18n.getTranslation("no")
                ),
            ]),
        ]
    );

    Elem.parentElement.parentElement.appendChild(faqAnswer);
    Elem.parentElement.parentElement.appendChild(faqConfirm);
}

export async function uploadFile(claim, s3env, upload, index, retryCount) {
    console.log(`uploadFile init...`);

    retryCount++;

    const params = {
        domain: claim.data.domain,
        filename: upload.file.name,
        type: upload.file.type,
        extension: upload.file.name.split(".").pop(),
        description: upload.description,
        timestamp: Date.now(),
        index: index,
    };

    // Add Your Key Here!!!
    if (axios.defaults.headers.common["x-api-key"] == undefined) {
        axios.defaults.headers.common = {
            "Content-Type": "application/json",
            "x-api-key": appConfigKey,
        };
    }

    const res = await axios.get(s3env + "/s3Uploader", { params });

    const tags = {
        domain: claim.data.domain,
        name: safeTag(upload.file.name),
        description: safeTag(upload.description),
    };

    // Build tag query string
    const tag_query = Object.keys(tags)
        .map(function (k) {
            return encodeURIComponent(k) + "=" + encodeURIComponent(tags[k]);
        })
        .join("&");
    // S3 signed URL headers
    const options = {
        headers: {
            "Content-Type": upload.file.type,
            "x-amz-tagging": tag_query,
        },
    };
    // Put file to S3
    let s3post;
    let errorMessage = "";
    try {
        s3post = await axios.put(res.data.uploadURL, upload.file, options);
    } catch (error) {
        errorMessage = error;
    }

    if (s3post.status == 200) {
        console.log("s3post status 200...");
        //if succeeded, write to claim_files
        const file = {
            filename: res.data.fileName,
            description: upload.description,
            filetype: upload.file.type,
            error: undefined,
            name: upload.file.name,
            failed: false,
            hash: upload.hash,
            doc_id: upload.doc_id || "submit-claim",
        };

        claim.data.files.push(file);
    } else {
        //if failed recursivelly call 5x, then abort
        //retry 3x
        if (retryCount <= 3) {
            console.log("s3post failure, trying again...");
            wait(1000);
            uploadFile(upload, index, retryCount);
        } else {
            if (errorMessage == "") errorMessage = s3post;
            const fileData = {
                filename: filename,
                description: upload.description,
                filetype: upload.file.type,
                name: upload.file.name,
                // error: JSON.stringify(s3post),
                error: JSON.stringify(errorMessage),
                failed: true,
                hash: upload.hash,
                doc_id: upload.doc_id || "submit-claim",
            };
            claim.data.files.push(fileData);
        }
    }
}
